import React, { useState, useEffect } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faAngleLeft,
  faAngleRight,
  faSpinner,
  faFileInvoice,
  faXmark,
  faSync,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { BASE_URL } from '../../config';

const AdminFee = () => {
  // State for tabs
  const [activeTab, setActiveTab] = useState('SI');

  // State for filters
  const [filters, setFilters] = useState({
    corporate: [],
    memberType: 'ALL',
    dateCreditedFrom: '',
    dateCreditedTo: '',
  });

  // State for corporate search
  const [corporateSearch, setCorporateSearch] = useState('');
  const [filteredCorporations, setFilteredCorporations] = useState([]);

  // State for table
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  // State for checkboxes
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // State for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [billNumber, setBillNumber] = useState('');

  // Formatter for currency
  const phpFormatter = new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Fetch corporations
  const fetchCorporations = async (search = '') => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/admin-fee/corporations`, {
        params: { search, billing_type: activeTab },
        headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
      });
      setFilteredCorporations(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch corporations.', { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // Handle corporate search
  // const handleCorporateSearch = (e) => {
  //   const value = e.target.value;
  //   setCorporateSearch(value);
  //   fetchCorporations(value);
  // };
  // Update handleCorporateSearch to only fetch when there's input
  const handleCorporateSearch = (e) => {
    const value = e.target.value;
    setCorporateSearch(value);
    if (value.trim()) {
      fetchCorporations(value);
    } else {
      setFilteredCorporations([]);
    }
  };

  // Handle corporate selection
  const handleCorporateSelect = (corp) => {
    if (filters.corporate.some((c) => c.id === corp.id)) {
      toast.error(`"${corp.name}" is already selected.`, { duration: 3000 });
      setCorporateSearch('');
      setFilteredCorporations([]);
      return;
    }
    setFilters((prev) => ({
      ...prev,
      corporate: [...prev.corporate, corp],
    }));
    setCorporateSearch('');
    setFilteredCorporations([]);
  };

  // Remove corporate filter
  const handleRemoveCorporate = (corpId) => {
    setFilters((prev) => ({
      ...prev,
      corporate: prev.corporate.filter((c) => c.id !== corpId),
    }));
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    setSelectedTransactions([]);
    setSelectAll(false);
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFilters({
      corporate: [],
      memberType: 'ALL',
      dateCreditedFrom: '',
      dateCreditedTo: '',
    });
    setCorporateSearch('');
    setFilteredCorporations([]);
    setSelectedTransactions([]);
    setSelectAll(false);
    setCurrentPage(1);
  };

  // Handle checkbox selection
  const handleCheckboxChange = (transId) => {
    setSelectedTransactions((prev) => {
      if (prev.includes(transId)) {
        setSelectAll(false);
        return prev.filter((id) => id !== transId);
      }
      const newSelected = [...prev, transId];
      const allCurrentPageSelected = transactions.every((txn) =>
        newSelected.includes(txn.id)
      );
      setSelectAll(allCurrentPageSelected && newSelected.length === totalEntries);
      return newSelected;
    });
  };

  // Handle select all
  const handleSelectAll = async () => {
    if (selectAll) {
      setSelectedTransactions([]);
      setSelectAll(false);
    } else {
      try {
        setSelectAllLoading(true);
        const response = await axios.get(`${BASE_URL}/admin-fee/all-transaction-ids`, {
          params: {
            corporate: filters.corporate.map((c) => c.id),
            member_type: filters.memberType,
            date_credited_from: filters.dateCreditedFrom,
            date_credited_to: filters.dateCreditedTo,
            billing_type: activeTab,
          },
          headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
        });
        const allTransactionIds = response.data.data;
        setSelectedTransactions(allTransactionIds);
        setSelectAll(true);
        toast.success(`Selected all ${allTransactionIds.length} transactions.`, { duration: 3000 });
      } catch (error) {
        toast.error('Failed to select all transactions.', { duration: 3000 });
      } finally {
        setSelectAllLoading(false);
      }
    }
  };

  // Handle generate billing button click
  const handleGenerateBillingClick = () => {
    if (selectedTransactions.length === 0) {
      toast.error('Please select at least one transaction.', { duration: 3000 });
      return;
    }
    setShowConfirmModal(true);
  };

  // Handle confirm billing
  const handleConfirmBilling = async () => {
    if (!billNumber || billNumber <= 0) {
      toast.error(`Please enter a valid ${activeTab === 'SI' ? 'Sales Invoice' : 'Statement of Account'} number.`, { duration: 3000 });
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/admin-fee/generate-billing`,
        {
          selected_transactions: selectedTransactions,
          billing_type: activeTab,
          bill_number: parseInt(billNumber),
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` } }
      );
      toast.success(response.data.message, { duration: 3000 });
      setShowConfirmModal(false);
      setBillNumber('');
      setSelectedTransactions([]);
      setSelectAll(false);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate billing.', { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // Fetch transactions
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/admin-fee`, {
        params: {
          corporate: filters.corporate.map((c) => c.id),
          member_type: filters.memberType !== 'ALL' ? filters.memberType : undefined,
          date_credited_from: filters.dateCreditedFrom,
          date_credited_to: filters.dateCreditedTo,
          billing_type: activeTab,
          page: currentPage,
          per_page: itemsPerPage,
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
      });
      setTransactions(response.data.data);
      setTotalEntries(response.data.pagination.total_records);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error('Failed to fetch transactions.', { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // Handle refresh table
  const handleRefresh = () => {
    fetchTransactions();
    setSelectedTransactions([]);
    setSelectAll(false);
    toast.success('Table refreshed.', { duration: 3000 });
  };

  // Update selectAll state
  useEffect(() => {
    if (transactions.length > 0) {
      const allCurrentPageSelected = transactions.every((txn) =>
        selectedTransactions.includes(txn.id)
      );
      setSelectAll(allCurrentPageSelected && selectedTransactions.length === totalEntries);
    } else {
      setSelectAll(false);
    }
  }, [selectedTransactions, transactions, totalEntries]);

  // Fetch transactions when filters, tab, or pagination change
  useEffect(() => {
    fetchTransactions();
  }, [filters, activeTab, currentPage, itemsPerPage]);

  // eto yung automatic na nagpapalabas sa dropdown sa unang load nung page or components
  // Fetch corporations on tab change
  // useEffect(() => {
  //   fetchCorporations();
  // }, [activeTab]);

  // Table columns
  const columns = [
    {
      key: 'checkbox',
      label: (
        <input
          type="checkbox"
          checked={selectAll}
          onChange={handleSelectAll}
          disabled={selectAllLoading}
          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
        />
      ),
      sortable: false,
    },
    { key: 'seriesNo', label: 'No.', sortable: false },
    { key: 'admission_date', label: 'Admission', sortable: false },
    { key: 'trans_no', label: 'Trans No.', sortable: false },
    { key: 'loa', label: 'LOA', sortable: false },
    { key: 'member_name', label: 'Name', sortable: false },
    { key: 'member_id', label: 'Member ID', sortable: false },
    { key: 'member_type', label: 'Type', sortable: false },
    { key: 'transaction_amt', label: 'Trans Amt.', sortable: false },
    { key: 'actual_claim_amt', label: 'Claim Amt.', sortable: false },
    { key: 'withholding_tax', label: 'Tax', sortable: false },
    { key: 'net_payable', label: 'Net', sortable: false },
    { key: 'corporate', label: 'Corporate', sortable: false },
    { key: 'date_credited', label: 'Credited', sortable: false },
    { key: 'payment_ref_no', label: 'Ref No.', sortable: false },
  ];

  // Pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const getShowingEntriesText = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalEntries);
    return `Showing ${start} to ${end} of ${totalEntries.toLocaleString()} entries`;
  };

  // Loading Skeleton
  const LoadingSkeleton = ({ rows = 5, columns = 15 }) => (
    <>
      {Array(rows).fill().map((_, i) => (
        <tr key={i} className="animate-pulse border-b border-gray-100">
          {Array(columns).fill().map((_, j) => (
            <td key={j} className="p-2">
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-indigo-600 text-white rounded-lg">
            <FontAwesomeIcon icon={faSearch} className="text-sm" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Admin Fee</h1>
            <p className="text-xs text-gray-500">Generate and manage admin fee billings</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            {['SI', 'SOA'].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors duration-200 ${
                  activeTab === tab
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tab === 'SI' ? 'Sales Invoice' : 'Statement of Account'}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="p-4 bg-gray-50 border-b border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Corporate Filter */}
              <div className="relative">
                <label className="block text-xs font-medium text-gray-700 mb-1">Corporate</label>
                <input
                  type="text"
                  placeholder="Search corporate..."
                  value={corporateSearch}
                  onChange={handleCorporateSearch}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                />
                {filteredCorporations.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-lg">
                    {filteredCorporations.map((corp) => (
                      <li
                        key={corp.id}
                        onClick={() => handleCorporateSelect(corp)}
                        className="px-3 py-1.5 text-sm hover:bg-indigo-50 cursor-pointer"
                      >
                        {corp.id} - {corp.name}
                      </li>
                    ))}
                  </ul>
                )}
                {filters.corporate.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {filters.corporate.map((corp) => (
                      <span
                        key={corp.id}
                        className="inline-flex items-center px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded-full"
                      >
                        {corp.name}
                        <button
                          onClick={() => handleRemoveCorporate(corp.id)}
                          className="ml-1 text-indigo-600 hover:text-indigo-800"
                        >
                          <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Member Type */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Member Type</label>
                <select
                  value={filters.memberType}
                  onChange={(e) => handleFilterChange('memberType', e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                >
                  <option value="ALL">All</option>
                  <option value="Principal">Principal</option>
                  <option value="Dependent">Dependent</option>
                </select>
              </div>

              {/* Date Range */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Date Credited - From</label>
                  <input
                    type="date"
                    value={filters.dateCreditedFrom}
                    onChange={(e) => handleFilterChange('dateCreditedFrom', e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Date Credited - To</label>
                  <input
                    type="date"
                    value={filters.dateCreditedTo}
                    onChange={(e) => handleFilterChange('dateCreditedTo', e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="p-4">
            <div className="flex justify-end mb-2">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className={`px-3 py-1.5 text-xs text-white rounded-lg flex items-center gap-1 transition ${
                  loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                <FontAwesomeIcon icon={faSync} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    {columns.map((column) => (
                      <th key={column.key} className="p-2 text-left font-semibold text-gray-600">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
                  ) : transactions.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="p-2 text-center text-gray-500">
                        No transactions found.
                      </td>
                    </tr>
                  ) : (
                    transactions.map((txn, index) => (
                      <tr key={txn.id} className="hover:bg-gray-50 transition">
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={selectedTransactions.includes(txn.id)}
                            onChange={() => handleCheckboxChange(txn.id)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                        </td>
                        <td className="p-2">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td className="p-2">{txn.admission_date}</td>
                        <td className="p-2">{txn.trans_no}</td>
                        <td className="p-2">{txn.loa}</td>
                        <td className="p-2">{txn.member_name}</td>
                        <td className="p-2">{txn.member_id}</td>
                        <td className="p-2">{txn.member_type}</td>
                        <td className="p-2">{phpFormatter.format(txn.transaction_amt)}</td>
                        <td className="p-2">{phpFormatter.format(txn.actual_claim_amt)}</td>
                        <td className="p-2">{phpFormatter.format(txn.withholding_tax)}</td>
                        <td className="p-2">{phpFormatter.format(txn.net_payable)}</td>
                        <td className="p-2">{txn.corporate}</td>
                        <td className="p-2">{txn.date_credited}</td>
                        <td className="p-2">{txn.payment_ref_no}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination and Actions */}
          <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="px-2 py-1 border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
              >
                {[5, 10, 20, 50, 100].map((val) => (
                  <option key={val} value={val}>{val}/page</option>
                ))}
              </select>
              <span>{getShowingEntriesText()}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-xs text-gray-600">{currentPage} of {totalPages}</span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleGenerateBillingClick}
                disabled={selectedTransactions.length === 0 || loading || selectAllLoading}
                className={`px-3 py-1.5 text-xs text-white rounded-lg flex items-center gap-1 transition ${
                  selectedTransactions.length === 0 || loading || selectAllLoading
                    ? 'bg-green-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                <FontAwesomeIcon icon={faFileInvoice} /> Generate Billing
              </button>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 w-full max-w-sm">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Confirm Billing</h2>
              <p className="text-xs text-gray-600 mb-3">
                Generate <span className="font-bold">{activeTab === 'SI' ? 'Sales Invoice' : 'Statement of Account'}</span> for <span className="font-bold">{selectedTransactions.length}</span> transaction(s).
              </p>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {activeTab === 'SI' ? 'Sales Invoice' : 'Statement of Account'} Number
              </label>
              <input
                type="number"
                value={billNumber}
                onChange={(e) => setBillNumber(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 mb-3"
                placeholder={`Enter ${activeTab} number`}
                min="1"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setShowConfirmModal(false); setBillNumber(''); }}
                  className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmBilling}
                  disabled={loading}
                  className={`px-3 py-1.5 text-xs text-white rounded-lg flex items-center gap-1 ${
                    loading ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {loading && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFee;